// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { ApiServiceError } from "./apiError";
import { AuthTokenUnavailableError, SIGNED_OUT_MESSAGE } from "./demoData";

export const PERMISSION_DENIED_MESSAGE = "You don't have permission to do this.";

const RAW_API_FAILURE = /^API request failed \(\d+\)/;
const MAX_SAFE_MESSAGE_LENGTH = 200;

function readServerMessage(error: ApiServiceError): string | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(error.responseBody);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== "object") {
    return undefined;
  }
  const body = parsed as {
    error?: { message?: unknown; detail?: unknown };
    detail?: unknown;
    title?: unknown;
  };
  const candidates = [
    body.error?.message,
    body.error?.detail,
    body.detail,
    body.title,
  ];
  return candidates.find(
    (value): value is string =>
      typeof value === "string" && isSafeMessage(value),
  );
}

function isSafeMessage(message: string): boolean {
  const trimmed = message.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= MAX_SAFE_MESSAGE_LENGTH &&
    !RAW_API_FAILURE.test(trimmed) &&
    !/^[[{]/.test(trimmed) &&
    !/\bat\s+\S+\s*\(|\n\s+at\s|Exception\b|SqlClient|SELECT\s|INSERT\s|UPDATE\s/i.test(
      trimmed,
    )
  );
}

export function toUserFacingErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof ApiServiceError) {
    if (error.status === 401) {
      return SIGNED_OUT_MESSAGE;
    }
    if (error.status === 403) {
      return PERMISSION_DENIED_MESSAGE;
    }
    if (error.status === 404) {
      return "We couldn't find that. It may have been moved or deleted.";
    }
    if (error.status >= 400 && error.status < 500) {
      return readServerMessage(error) ?? fallback;
    }
    return fallback;
  }
  if (error instanceof AuthTokenUnavailableError) {
    return error.message;
  }
  if (error instanceof TypeError) {
    return "Can't reach Riddle right now. Check your connection and try again.";
  }
  if (error instanceof Error && isSafeMessage(error.message)) {
    return error.message.trim();
  }
  return fallback;
}
